import React, { useContext, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, AuthContext } from "./context/AuthContext";
import {
  AdminAuthProvider,
  AdminAuthContext,
} from "./context/AdminAuthContext";
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { installSafeAreaVarSync } from "./lib/safeAreaVars";
import { installKeyboardReveal } from "./lib/keyboardReveal";
import { hideBootSpinner, markTalentDevice } from "./lib/bootSpinner";
import Layout from "./layout/Layout";
import Home from "./pages/Home";
import WelcomeTourPage from "./pages/WelcomeTourPage";
import JoinPage from "./pages/JoinPage";
import FirstRunTourPage from "./pages/FirstRunTourPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import LoginPage from "./pages/LoginPage";
import DJPublicPage from "./pages/DJPublicPage";
import RequestPage from "./pages/RequestPage";
import DJQrPage from "./pages/DJQrPage";
import StripeCallbackPage from "./pages/StripeCallbackPage";
import InstagramCallbackPage from "./pages/InstagramCallbackPage";
import StripeResumePage from "./pages/StripeResumePage";
import FinishStripePage from "./pages/FinishStripePage";
import StripeRejectedPage from "./pages/StripeRejectedPage";
import LegacySuccessRedirect from "./pages/LegacySuccessRedirect";
import OpenLinkRedirect from "./pages/OpenLinkRedirect";
import ReceiptPage from "./pages/ReceiptPage";
import RequestsPage from "./pages/RequestsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { track } from "./lib/analytics";
import NotFoundPage from "./pages/NotFoundPage";
import Loader from "./components/Loader";
import { RequireAuth, RequireNoAuth } from "./components/RouteGuards";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import { RequireAdminAuth } from "./components/RequireAdminAuth";
import AdminErrorBoundary from "./components/AdminErrorBoundary";
import TermsOfService from "./pages/TermsOfService";
// AdminDjPage removed - using AdminDjProfile instead
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AboutPage from "./pages/AboutPage";
import DownloadPage from "./pages/DownloadPage";
import SmsOptInPage from "./pages/SmsOptInPage";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GOOGLE_CLIENT_ID } from "./config";
import AdminDjProfile from "./pages/AdminDjProfile";
import AdminReviewPage from "./pages/AdminReviewPage";
import PayoutHistoryPage from "./pages/PayoutHistoryPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import BalancePage from "./pages/BalancePage";
import PlaylistPage from "./pages/PlaylistPage";
import EstablishmentPage from "./pages/EstablishmentPage";
import FanLoginPage from "./pages/FanLoginPage";
import FanClaimPage from "./pages/FanClaimPage";
import FanNamePage from "./pages/FanNamePage";
import FanHomePage from "./pages/FanHomePage";
import FanRequestsPage from "./pages/FanRequestsPage";
import FanLeaderboardPage from "./pages/FanLeaderboardPage";
import DisputeUploadPage from "./pages/DisputeUploadPage";
import ClaimRespondPage from "./pages/ClaimRespondPage";
import ChatBot from "./components/ChatBot";
import DeepLinkHandler from "./components/DeepLinkHandler";
import EmailClickTracker from "./components/EmailClickTracker";
import StripeSelfCompletion from "./components/StripeSelfCompletion";
import BiometricEnrollPrompt from "./components/BiometricEnrollPrompt";
import WalletMoment from "./components/WalletMoment";
import AdminManifestSwap from "./components/AdminManifestSwap";
import { APP_FIRST_SEGMENTS } from "./lib/customerPages";
import { useLocation } from "react-router-dom";

const AppStoreRedirect = () => {
  window.location.replace("https://apps.apple.com/app/id6786675170");
  return null;
};

const ChatBotConditional: React.FC = () => {
  const location = useLocation();
  const path = location.pathname;

  // "/fan/" + exact "/fan": every fan surface (app fan mode) — the DJ
  // support chatbot has no business floating over fans.
  const hideOn = ["/dj/", "/success", "/free-success", "/receipt/", "/venue/", "/requests", "/disputes/", "/claims/", "/welcome", "/first-run-tour", "/fan/"];
  if (path === "/fan" || hideOn.some((p) => path.startsWith(p))) return null;

  // Public request page is reached via `/:slug`. Any single-segment path
  // that isn't a known DJ/app route is a client-facing request page — hide.
  // (Shared with Header's Login/Sign-Up hiding — see lib/customerPages.ts.)
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 1 && !APP_FIRST_SEGMENTS.has(segments[0])) return null;

  return <ChatBot />;
};

const AppContent: React.FC = () => {
  const { loading, user } = useContext(AuthContext);
  const { loading: adminLoading, admin } = useContext(AdminAuthContext);

  useEffect(() => {
    // Growth funnel: every cold start / page load is an open (resume after a
    // long idle is handled inside lib/analytics).
    track("app_opened");
    if (Capacitor.isNativePlatform()) {
      // Edge-to-edge: let the webview extend under the iOS status bar so the
      // green header paints to the physical top of the screen. Layout then
      // ducks below the clock via env(safe-area-inset-top) in CSS — plain,
      // reliable layout instead of native frame-resizing, which iOS
      // mis-measures when returning from another app (the "◀ TestFlight"
      // breadcrumb overlap bug). Web is untouched: env() reads 0 in browsers.
      StatusBar.setOverlaysWebView({ overlay: true });
      StatusBar.setStyle({ style: Style.Dark });
      // viewport-fit=cover lives statically in public/index.html — setting it
      // here at runtime was flaky (env() sometimes read 0 on cold start /
      // resume → header mashed into the status bar, founder report 1.1(12)).
      // Static viewport-fit fixed cold start, but env() STILL goes stale on
      // app-switch resume (founder report 1.1(13)) — this mirrors env() into
      // --safe-top/--safe-bottom vars that survive the glitch.
      installSafeAreaVarSync();
    }
    // The keyboard events only ever fire in the phone apps; on the web this
    // listens for nothing. Sheets are laid out for the keyboard as it opens
    // on iOS, where the webview shrinks only after it has risen (measured in
    // the simulator); Android keeps the plain reveal. See lib/keyboardReveal.ts.
    installKeyboardReveal({ fitSheets: Capacitor.getPlatform() !== "android" });
  }, []);

  // The boot screen stays up until the sign-in check is done, then leaves for
  // the real screen (on a cold open of the phone apps the wordmark flies
  // through, 1.5.3): one launch screen, no logo-only flash in between
  // (founder, 1.5.2 b47). The Loader under it only shows if the boot screen
  // has already gone (its 15 s safety net, or a later reload of the check).
  const ready = !loading && !adminLoading;
  useEffect(() => {
    if (ready) hideBootSpinner("app");
  }, [ready]);
  // A talent has signed in on this device: from the next launch the boot
  // screen may show the talent slogan at "/" (public/index.html), even on a
  // phone that never saw the "I perform / I'm a fan" screen.
  useEffect(() => {
    if (user) markTalentDevice();
  }, [user]);

  if (!ready) return <Loader />;

  return (
    <>
      <DeepLinkHandler />
      {/* Talent email buttons carry ?em=<email name>: logged once, then em
          leaves the address bar. Before <Routes>, so the tap is logged before
          a page's redirect (/open/payouts, the sign-in guard) moves on. */}
      <EmailClickTracker />
      <StripeSelfCompletion />
      {/* Face ID / Touch ID sign-in: one-time "Set up Face ID?" after the first
          sign-in on this install + silent re-arm for opted-in accounts. Native
          only — renders nothing on web/desktop. */}
      <BiometricEnrollPrompt />
      <WalletMoment />
      <AdminManifestSwap />
      <Routes>
        <Route
          path="/admin/login"
          element={
            !admin ? <AdminLogin /> : <Navigate to="/admin/dashboard" replace />
          }
        />
        <Route
          path="/admin/dashboard/*"
          element={
            <RequireAdminAuth>
              <AdminErrorBoundary>
                <AdminDashboard />
              </AdminErrorBoundary>
            </RequireAdminAuth>
          }
        />
        <Route
          path="/admin/dj/:id"
          element={
            <RequireAdminAuth>
              <AdminDjProfile />
            </RequireAdminAuth>
          }
        />
        {/* Approve / Decline buttons in the founder's "New talent to review" email. */}
        <Route
          path="/admin/review/:id"
          element={
            <RequireAdminAuth>
              <AdminReviewPage />
            </RequireAdminAuth>
          }
        />
        <Route path="/venue/:slug" element={<EstablishmentPage />} />
        {/* Native welcome tour — full-screen intro, deliberately outside
            Layout so no web header/footer chrome renders over it. Home
            redirects logged-out native users here (build 32). */}
        <Route path="/welcome" element={<WelcomeTourPage />} />
        {/* Refer & Earn link: remembers the code, then hands off to signup. */}
        <Route path="/join/:code" element={<JoinPage />} />
        {/* Talent emails' website-only doors: forward to /requests?payouts=1
            and /requests?refer=1. Outside the guards (RequireAuth on /requests
            handles sign-in) and NEVER in the app-site-association file, so an
            iPhone opens them in Safari (the 1.5.1 app doesn't know these links). */}
        <Route path="/open/payouts" element={<OpenLinkRedirect flag="payouts" />} />
        <Route path="/open/refer" element={<OpenLinkRedirect flag="refer" />} />
        {/* First-run tour — post-login/post-Stripe intro, same
            full-screen treatment as the welcome tour. RouteGuards' FirstRunTourGate
            routes each account here exactly once; the page guards auth itself. */}
        <Route path="/first-run-tour" element={<FirstRunTourPage />} />
        {/* App fan mode onboarding (locked r6 §4) — full-screen like the
            tour, outside Layout: role screen's "I'm a fan" lands on claim,
            claim/skip lands on the nickname screen or fan home. */}
        <Route path="/fan/claim" element={<FanClaimPage />} />
        <Route path="/fan/name" element={<FanNamePage />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          {/* The Learn More page is retired (founder) — old links land home. */}
          <Route path="learn-more" element={<Navigate to="/" replace />} />
          <Route path="dj/:slug" element={<DJPublicPage />} />
          <Route path="dj/:slug/request" element={<RequestPage />} />
          {/* Retired (founder pick 1A): the confirmation is the live
              card on My Requests. Old links forward with their params intact. */}
          <Route path="success" element={<LegacySuccessRedirect />} />
          <Route path="/free-success" element={<LegacySuccessRedirect />} />
          <Route path="receipt/:receiptId" element={<ReceiptPage />} />
          {/* Setlist sales — the fan's delivered-playlist page. Public: the
              receiptId in the URL is the access token (receipt convention). */}
          <Route path="playlist/:receiptId" element={<PlaylistPage />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsOfService />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="download" element={<DownloadPage />} />
          {/* requesta.app/app — our one "get the app" share link. The server
              side (api/app-link.ts) answers real visits; this only catches an
              in-app navigation to the same path. */}
          <Route path="app" element={<AppStoreRedirect />} />
          <Route path="sms" element={<SmsOptInPage />} />
          {/* Fan accounts: where the sign-in magic-link email lands. Public
              — identity is the single-use token in the URL. */}
          <Route path="fan/login" element={<FanLoginPage />} />
          {/* App fan mode tabs (locked r6 §5). Inside Layout on purpose:
              the customer-page Header (wordmark + fan chip + its sheets)
              is exactly the fan-mode chrome; FanTabBar adds the 3-tab bar. */}
          <Route path="fan" element={<FanHomePage />} />
          <Route path="fan/requests" element={<FanRequestsPage />} />
          <Route path="fan/leaderboard" element={<FanLeaderboardPage />} />
          <Route path="fan/*" element={<NotFoundPage />} />
          {/* Public + token-gated: the EMAIL 1 resume link. Identity comes from
              the token in the URL, so it sits outside the auth guards (a logged-out
              talent must reach it; a logged-in one shouldn't be bounced away). */}
          <Route path="register/stripe/resume" element={<StripeResumePage />} />
          <Route element={<RequireNoAuth />}>
            <Route path="register" element={<OnboardingPage />} />
            <Route path="register/stripe/callback" element={<StripeCallbackPage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password/:token" element={<ResetPasswordPage />} />
          </Route>
          {/* "Continue with Instagram" lands here. Deliberately outside the
              signed-in guard: on the phone this page renders inside the
              browser sheet, which carries no session — it only hands control
              back to the app. Declared before :slug so the fan page can't
              swallow it. */}
          <Route path="instagram/callback" element={<InstagramCallbackPage />} />
          <Route path=":slug" element={<RequestPage />} />
          <Route element={<RequireAuth />}>
            {/* Profile is the #profile drawer now, not a page.
                Old /profile links (emails, bookmarks, pre-drawer builds) open
                the drawer over Requests instead of 404ing. */}
            <Route path="profile" element={<Navigate to="/requests#profile" replace />} />
            <Route path="balance" element={<BalancePage />} />
            {/* "More" merged into Profile long ago; forward its lingering
                links to the same drawer. */}
            <Route path="more" element={<Navigate to="/requests#profile" replace />} />
            <Route path="requests" element={<RequestsPage />} />
            <Route path="payout-history" element={<PayoutHistoryPage />} />
            <Route path="leaderboard" element={<LeaderboardPage />} />
            <Route path="qr/:slug" element={<DJQrPage />} />
            <Route path="disputes/:id/upload" element={<DisputeUploadPage />} />
            {/* "Wasn't played?" — the talent answers a fan's claim (banner, push, email). */}
            <Route path="claims/:id" element={<ClaimRespondPage />} />
            {/* PR 2 — Stripe-recovery destinations. Both are post-login states:
                login itself succeeded, but the server flagged the talent as
                mid-Stripe (finish-stripe) or terminally-rejected (stripe-rejected). */}
            <Route path="finish-stripe" element={<FinishStripePage />} />
            <Route path="stripe-rejected" element={<StripeRejectedPage />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ChatBotConditional />
    </>
  );
};

export default function App() {
  if (!GOOGLE_CLIENT_ID) {
    console.error(
      "Google CLIENT_ID is missing. Set REACT_APP_GOOGLE_CLIENT_ID in client/.env for local dev and in Vercel Environment Variables for deployed builds."
    );
  }

  return (
    // react-router 6.28+ prints a console warning for every v7 "future" flag
    // left undefined. Both are pinned to `false` = today's v6 behaviour,
    // stated on purpose; opting in is a decision for a v7 upgrade, not a
    // side effect of a security bump.
    <BrowserRouter
      future={{ v7_startTransition: false, v7_relativeSplatPath: false }}
    >
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || "missing"}>
        <AuthProvider>
          <AdminAuthProvider>
            <AppContent />
          </AdminAuthProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </BrowserRouter>
  );
}
